import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search,
  ShoppingCart,
  Plus,
  Minus,
  X,
  Trash2,
  CreditCard,
  Banknote,
  Check,
  Package,
  Scale,
  ShieldAlert,
  Pencil,
} from "lucide-react";
import Layout from "../components/Layout";
import Modal from "../components/Modal";
import Numpad from "../components/Numpad";
import { useAuth } from "../context/AuthContext";
import { formatNPR, formatQty, formatSize, CATEGORIES } from "../lib/utils";
import {
  BASIS_PIECE,
  BASIS_DOZEN,
  BASIS_WEIGHT,
  deriveFloor,
  floorForBasis,
  piecesSold,
  priceStanding,
} from "../lib/pricing";
import { enqueue, getQueue, onSynced } from "../lib/outbox";
import { fetchWithSnapshot, applyPendingWrites, KEYS } from "../lib/snapshot";
import { productMatchesQuery } from "../lib/search";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";
const API = `${API_BASE}/api`;

/**
 * What this item usually goes for.
 *
 * The tag price is an opening ask, not a selling price, so prefilling it would
 * mean editing the rate on every single sale. The middle of the last few agreed
 * prices is a much better guess, and it gets better the longer the shop runs.
 */
function usualRate(product) {
  const recent = (product.recent_prices || []).filter((n) => n > 0);
  if (!recent.length) return product.mrp || 0;
  const sorted = [...recent].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)];
}

const BASIS_LABELS = {
  [BASIS_PIECE]: { en: "Piece", np: "थान" },
  [BASIS_DOZEN]: { en: "Dozen", np: "दर्जन" },
  [BASIS_WEIGHT]: { en: "Weight", np: "तौल" },
};

const STANDING_STYLES = {
  below: "bg-red-50 text-red-700 border-red-200",
  thin: "bg-amber-50 text-amber-800 border-amber-200",
  good: "bg-green-50 text-green-700 border-green-200",
  full: "bg-green-50 text-green-700 border-green-200",
};

export default function Sale() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [discount, setDiscount] = useState("0");
  const [paymentType, setPaymentType] = useState("cash");
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offline, setOffline] = useState(false);

  const [units, setUnits] = useState({});
  const [sizeUnits, setSizeUnits] = useState({});
  const [shop, setShop] = useState(null);

  // The line being priced: {product, basis, qty, rate, lineId?}
  const [editing, setEditing] = useState(null);
  const [numpadTarget, setNumpadTarget] = useState(null); // "qty" | "rate"
  const [numpadValue, setNumpadValue] = useState("");

  // Asked for only when a line is under its floor, and never kept.
  const [pinPrompt, setPinPrompt] = useState(null);
  const [pin, setPin] = useState("");

  const minMargin = Number(shop?.min_margin_percent) || 10;

  useEffect(() => {
    if (search.trim()) {
      setFilteredProducts(products.filter((p) => productMatchesQuery(p, search)));
    } else {
      setFilteredProducts(products);
    }
  }, [search, products]);

  // getAuthHeader is a fresh closure on every AuthContext render, so depending
  // on it directly would give fetchProducts a new identity each render and the
  // effect below would refetch in a loop.
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const fetchProducts = useCallback(async () => {
    try {
      // Falls back to the last saved copy when the server cannot be reached --
      // selling is the one thing that must keep working without a connection.
      const { data, stale } = await fetchWithSnapshot(KEYS.products, async () => {
        const res = await axios.get(`${API}/products`, authRef.current());
        return res.data;
      });

      // Queued sales have already left the shelf, so show quantities net of
      // them or the same last item can be sold twice.
      const adjusted = applyPendingWrites(data, getQueue());
      setProducts(adjusted);
      setFilteredProducts(adjusted);
      setOffline(stale);
    } catch (err) {
      // Only reached when there is no snapshot either -- a first run with no
      // connection. Nothing to show, so say so.
      toast.error("Failed to load products / सामान लोड भएन");
    } finally {
      setLoading(false);
    }
  }, []);

  // The floor cannot be drawn without the shop's minimum margin, and a size
  // cannot be rendered without its unit. Both are cached, so this works offline.
  const fetchReference = useCallback(async () => {
    const byId = (rows) => Object.fromEntries((rows || []).map((r) => [r.id, r]));
    try {
      const [u, su, sh] = await Promise.all([
        fetchWithSnapshot(KEYS.units, async () =>
          (await axios.get(`${API}/units`, authRef.current())).data),
        fetchWithSnapshot(KEYS.sizeUnits, async () =>
          (await axios.get(`${API}/size-units`, authRef.current())).data),
        fetchWithSnapshot(KEYS.shop, async () =>
          (await axios.get(`${API}/shop`, authRef.current())).data),
      ]);
      setUnits(byId(u.data));
      setSizeUnits(byId(su.data));
      setShop(sh.data);
    } catch (err) {
      // Falls back to the 10% default. The server enforces the real figure
      // regardless, so the worst case is a band drawn slightly wrong.
      console.error("Failed to load pricing reference:", err);
    }
  }, []);

  // Declared after fetchProducts on purpose: a const is in the temporal dead
  // zone until its initialiser runs, so referencing it in a dependency array
  // above this point throws on first render.
  useEffect(() => {
    fetchProducts();
    fetchReference();
  }, [fetchProducts, fetchReference]);

  // A sale that syncs in the background changes stock on the server; without
  // this the page would keep showing pre-sync quantities until navigation.
  useEffect(() => onSynced(fetchProducts), [fetchProducts]);

  // ---- pricing helpers for a line --------------------------------------------

  const bandFor = useCallback(
    (product, basis) => {
      const floorPerPiece = deriveFloor(
        product.cost_price,
        minMargin,
        product.min_price ?? null,
      );
      const floor = floorForBasis(floorPerPiece, basis, {
        packSize: product.pack_size,
        weightG: product.weight_g,
      });
      const perPiece = product.mrp || 0;
      const tag =
        basis === BASIS_DOZEN
          ? perPiece * (product.pack_size > 0 ? product.pack_size : 12)
          : basis === BASIS_WEIGHT && product.weight_g
            ? perPiece / (product.weight_g / 1000)
            : perPiece;
      return { floor, tag };
    },
    [minMargin],
  );

  /** Which bases this product can actually be sold on. */
  const basesFor = useCallback((product) => {
    const out = [BASIS_PIECE, BASIS_DOZEN];
    // Weight needs a piece weight, which only a purchase can establish. Offering
    // it without one would produce a line the server refuses.
    if (product.weight_g > 0) out.push(BASIS_WEIGHT);
    return out;
  }, []);

  // ---- cart ------------------------------------------------------------------

  /**
   * Put a line in the cart, or fold it into a matching one.
   *
   * `replacing` is the line being edited, which has to be taken out of the
   * shelf-limit sum before the new figure is checked against it -- otherwise
   * changing a line from 3 to 4 counts the original 3 as well and reads as an
   * oversell.
   */
  const addLine = (product, basis, qty, rate, replacing = null) => {
    const pieces = piecesSold(basis, qty, {
      packSize: product.pack_size,
      weightG: product.weight_g,
    });
    if (pieces === null) {
      toast.error("Weigh one piece first / एक थानको तौल चाहिन्छ");
      return false;
    }

    // Cap at what is on the shelf. Nothing downstream stops an oversell, and
    // the point of sale is where a person can actually see the shelf.
    const available = product.quantity || 0;
    const inCart = cart
      .filter((l) => l.product_id === product.id && l.line_id !== replacing)
      .reduce((n, l) => n + l.pieces, 0);
    const room = available - inCart;
    if (pieces > room + 1e-9) {
      toast.error(`Only ${formatQty(room)} left / ${formatQty(room)} मात्र बाँकी`);
      return false;
    }

    setCart((prev) => {
      const rest = replacing ? prev.filter((l) => l.line_id !== replacing) : prev;

      // Merge only when the product, the basis AND the rate all match. Merging
      // on the product alone would collapse two lines bargained to different
      // prices into one, silently changing what the customer was quoted.
      const same = rest.find(
        (l) => l.product_id === product.id && l.price_basis === basis && l.rate === rate,
      );
      if (same) {
        return rest.map((l) =>
          l.line_id === same.line_id
            ? {
                ...l,
                basis_quantity: l.basis_quantity + qty,
                pieces: l.pieces + pieces,
                total: (l.basis_quantity + qty) * rate,
              }
            : l,
        );
      }
      return [
        ...rest,
        {
          line_id: crypto.randomUUID(),
          product_id: product.id,
          product_name: product.name_en,
          name_np: product.name_np,
          size: formatSize(product, sizeUnits),
          unit: product.unit,
          price_basis: basis,
          basis_quantity: qty,
          pieces,
          rate,
          total: qty * rate,
        },
      ];
    });
    return true;
  };

  // Fast path: one piece at whatever it usually goes for.
  const quickAdd = (product) => {
    if (addLine(product, BASIS_PIECE, 1, usualRate(product))) {
      toast.success(`Added ${product.name_en}`);
    }
  };

  const openEditor = (product, line = null) => {
    setEditing({
      product,
      lineId: line?.line_id ?? null,
      basis: line?.price_basis ?? BASIS_PIECE,
      qty: String(line?.basis_quantity ?? 1),
      rate: String(line?.rate ?? usualRate(product)),
    });
  };

  const commitEditor = () => {
    const { product, lineId, basis, qty, rate } = editing;
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    if (q <= 0) {
      toast.error("Quantity must be more than zero");
      return;
    }
    // Stays open if the line was refused -- closing it would throw away what
    // the shopkeeper typed along with the reason it did not fit.
    if (addLine(product, basis, q, r, lineId)) setEditing(null);
  };

  const removeLine = (lineId) =>
    setCart((prev) => prev.filter((l) => l.line_id !== lineId));

  const stepLine = (lineId, change) => {
    setCart((prev) =>
      prev
        .map((l) => {
          if (l.line_id !== lineId) return l;
          const product = products.find((p) => p.id === l.product_id);
          const nextQty = l.basis_quantity + change;
          if (nextQty <= 0) return null;

          const perUnit = l.pieces / l.basis_quantity;
          const nextPieces = perUnit * nextQty;
          const available = product?.quantity ?? Infinity;
          const otherPieces = prev
            .filter((x) => x.product_id === l.product_id && x.line_id !== lineId)
            .reduce((n, x) => n + x.pieces, 0);
          if (nextPieces + otherPieces > available + 1e-9) {
            toast.error(`Only ${formatQty(available)} in stock`);
            return l;
          }
          return {
            ...l,
            basis_quantity: nextQty,
            pieces: nextPieces,
            total: nextQty * l.rate,
          };
        })
        .filter(Boolean),
    );
  };

  const subtotal = cart.reduce((sum, l) => sum + l.total, 0);
  const discountAmount = parseFloat(discount) || 0;
  const total = Math.max(0, subtotal - discountAmount);

  // Lines the server will refuse without an owner's PIN. Shown before checkout
  // so it is not a surprise at the last step.
  const belowFloor = useMemo(
    () =>
      cart.filter((l) => {
        const product = products.find((p) => p.id === l.product_id);
        if (!product) return false;
        const { floor } = bandFor(product, l.price_basis);
        return floor > 0 && l.rate < floor;
      }),
    [cart, products, bandFor],
  );

  // ---- checkout --------------------------------------------------------------

  const submitSale = async (overridePin = null) => {
    setSubmitting(true);

    // The id and timestamp are decided here, not by the server, so this sale
    // keeps its identity and its business day however long it waits in the
    // queue. See lib/outbox.js.
    //
    // Only what the till is entitled to decide is sent: the product, the basis,
    // how much and at what rate. Pieces, totals, cost and tag price are the
    // server's to work out.
    const payload = {
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      items: cart.map((l) => ({
        product_id: l.product_id,
        price_basis: l.price_basis,
        basis_quantity: l.basis_quantity,
        rate: l.rate,
      })),
      discount: discountAmount,
      payment_type: paymentType,
      customer_name: customerName || null,
      ...(overridePin ? { override_pin: overridePin } : {}),
    };

    const clearCart = () => {
      setCart([]);
      setDiscount("0");
      setCustomerName("");
      setShowCheckout(false);
      setPinPrompt(null);
      setPin("");
    };

    try {
      await axios.post(`${API}/sales`, payload, getAuthHeader());
      toast.success("Sale recorded! / बिक्री सफल!");
      clearCart();
      fetchProducts(); // Refresh stock
    } catch (err) {
      const status = err.response?.status;

      // The floor. Not a failure -- the owner can authorise it, so ask rather
      // than making the shopkeeper rebuild the sale at a higher price.
      if (status === 403) {
        setPinPrompt({ detail: err.response?.data?.detail || "Below the floor" });
        setSubmitting(false);
        return;
      }

      // Any other 4xx means the server understood and refused, so queueing it
      // would just fail again later -- surface it now. Anything else (no
      // response, 5xx) is the connection, and the sale belongs in the outbox.
      if (status && status >= 400 && status < 500) {
        toast.error(err.response?.data?.detail || "Failed to record sale");
        setSubmitting(false);
        return;
      }

      try {
        enqueue({
          method: "post",
          path: "/sales",
          body: payload,
          label: `${formatNPR(total)} · ${cart.length} item(s)`,
        });
        // Deliberately a success: from the shopkeeper's side the sale is
        // recorded and the money is in the till. Syncing is our problem.
        toast.success("Saved offline / अफलाइन सुरक्षित — sync हुनेछ");
        clearCart();
        // Re-read so the queued sale is subtracted from the displayed stock.
        // Without this the shelf still shows the item as available and it can
        // be sold again.
        fetchProducts();
      } catch (storageErr) {
        // Only if localStorage itself refused. Keep the cart so nothing is
        // silently lost.
        console.error("Could not queue sale:", storageErr);
        toast.error("Could not save sale / बिक्री सुरक्षित भएन");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error("Cart is empty / कार्ट खाली छ");
      return;
    }
    submitSale();
  };

  // ---- numpad ----------------------------------------------------------------

  const openNumpad = (target) => {
    setNumpadTarget(target);
    setNumpadValue(target === "qty" ? editing.qty : editing.rate);
  };

  const confirmNumpad = () => {
    setEditing((prev) => ({ ...prev, [numpadTarget]: numpadValue || "0" }));
    setNumpadTarget(null);
  };

  // ---- render ----------------------------------------------------------------

  const editingBand = editing ? bandFor(editing.product, editing.basis) : null;
  const editingRate = editing ? parseFloat(editing.rate) || 0 : 0;
  const editingStanding = editingBand
    ? priceStanding(editingRate, editingBand)
    : "good";
  const editingPieces = editing
    ? piecesSold(editing.basis, parseFloat(editing.qty) || 0, {
        packSize: editing.product.pack_size,
        weightG: editing.product.weight_g,
      })
    : null;

  return (
    <Layout>
      <div className="space-y-4 animate-fadeIn" data-testid="sale-page">
        {/* Saved copy in use. Selling still works, but stock may have moved on
            another device, so say so rather than present it as current. */}
        {offline && (
          <div
            className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-nepali"
            data-testid="stale-products-notice"
          >
            सुरक्षित सूची देखाइँदै — स्टक ताजा नहुन सक्छ
          </div>
        )}

        {/* Search */}
        <div className="flex items-center gap-2 bg-white rounded-xl px-4 h-12 border border-gray-200">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search — name, size, 22 gauge, tag code"
            className="flex-1 bg-transparent focus:outline-none text-sm"
            data-testid="sale-search-input"
          />
          {search && (
            <button onClick={() => setSearch("")}>
              <X className="w-5 h-5 text-gray-400" />
            </button>
          )}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredProducts.slice(0, 20).map((product) => {
              const size = formatSize(product, sizeUnits);
              return (
                <div
                  key={product.id}
                  className="bg-white rounded-xl p-3 border border-gray-100 cursor-pointer active:scale-95 transition-transform"
                  onClick={() => quickAdd(product)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    openEditor(product);
                  }}
                  data-testid={`sale-product-${product.id}`}
                >
                  <p className="font-medium text-[#2D2D2D] truncate text-sm">
                    {product.name_en}
                    {size && (
                      <span className="ml-1 text-[#8B0000] font-nepali">{size}</span>
                    )}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400 font-nepali">
                      {CATEGORIES[product.category]?.name_np}
                    </span>
                    <span className="font-bold text-[#8B0000] text-sm">
                      {formatNPR(product.mrp || 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span
                      className={`text-xs ${product.quantity <= product.low_stock_threshold ? "text-red-500" : "text-gray-400"}`}
                    >
                      Stock: {formatQty(product.quantity)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditor(product);
                      }}
                      className="p-1 bg-gray-100 rounded-lg"
                      title="Set quantity and price"
                      data-testid={`qty-btn-${product.id}`}
                    >
                      <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {filteredProducts.length === 0 && !loading && (
          <div className="empty-state py-8">
            <Package className="w-12 h-12 text-gray-300 mb-3" />
            <p className="text-gray-500">No products found</p>
          </div>
        )}

        {/* Cart Summary - Fixed Bottom */}
        {cart.length > 0 && (
          <div className="fixed bottom-20 left-4 right-4 bg-white rounded-2xl shadow-lg border border-gray-200 p-4 z-30">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-[#8B0000]" />
                <span className="font-semibold">{cart.length} items</span>
              </div>
              <span className="text-xl font-bold text-[#8B0000]">
                {formatNPR(subtotal)}
              </span>
            </div>

            {belowFloor.length > 0 && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-red-50 text-red-700 text-xs flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                {belowFloor.length} line(s) below the floor — owner's PIN needed
              </div>
            )}

            <button
              onClick={() => setShowCheckout(true)}
              className="w-full h-12 bg-[#8B0000] text-white font-semibold rounded-xl active:scale-95 transition-transform"
              data-testid="checkout-btn"
            >
              Checkout / बिल बनाउनुहोस्
            </button>
          </div>
        )}

        {/* Line editor: quantity, basis and the bargained rate */}
        <Modal
          isOpen={Boolean(editing) && !numpadTarget}
          onClose={() => setEditing(null)}
          title="Quantity & price"
          titleNp="संख्या र मूल्य"
        >
          {editing && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="font-semibold">
                  {editing.product.name_en}
                  <span className="ml-1.5 font-nepali text-[#8B0000]">
                    {formatSize(editing.product, sizeUnits)}
                  </span>
                </p>
                <p className="text-sm text-gray-500">
                  Tag {formatNPR(editing.product.mrp || 0)} · stock{" "}
                  {formatQty(editing.product.quantity)}
                </p>
              </div>

              {/* Sold by the piece, the dozen, or off the scale */}
              <div>
                <label className="text-sm text-gray-500 mb-2 block">
                  Charge by / कसरी बेच्ने
                </label>
                <div className="flex gap-2">
                  {basesFor(editing.product).map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setEditing((prev) => ({ ...prev, basis: b }))}
                      className={`flex-1 py-3 rounded-xl font-medium transition-colors text-sm ${
                        editing.basis === b
                          ? "bg-[#8B0000] text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                      data-testid={`basis-${b}`}
                    >
                      {b === BASIS_WEIGHT && (
                        <Scale className="w-4 h-4 inline mr-1 -mt-0.5" />
                      )}
                      {BASIS_LABELS[b].np}
                    </button>
                  ))}
                </div>
                {editing.basis === BASIS_WEIGHT && (
                  <p className="text-xs text-gray-400 mt-1">
                    Weigh it, type the kilos. Stock comes off in pieces —
                    about {formatQty(editing.product.weight_g)} g each.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">
                    {editing.basis === BASIS_WEIGHT ? "Kilos" : "How many"}
                  </label>
                  <button
                    type="button"
                    onClick={() => openNumpad("qty")}
                    className="w-full h-12 px-4 border border-gray-300 rounded-xl text-left font-semibold"
                    data-testid="edit-qty"
                  >
                    {editing.qty || "0"}
                  </button>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">
                    Price per {BASIS_LABELS[editing.basis].np}
                  </label>
                  <button
                    type="button"
                    onClick={() => openNumpad("rate")}
                    className="w-full h-12 px-4 border border-gray-300 rounded-xl text-left font-semibold"
                    data-testid="edit-rate"
                  >
                    {editing.rate || "0"}
                  </button>
                </div>
              </div>

              {/* The bargaining band. Where this price sits between the floor
                  and the tag, and what it earns. */}
              <div
                className={`p-3 rounded-xl border text-sm ${STANDING_STYLES[editingStanding]}`}
                data-testid="price-band"
              >
                <div className="flex items-center justify-between font-medium">
                  <span>
                    {editingStanding === "below" && "Below the floor"}
                    {editingStanding === "thin" && "Thin margin"}
                    {editingStanding === "good" && "Good price"}
                    {editingStanding === "full" && "Full price"}
                  </span>
                  <span>{formatNPR(Math.round(editingRate * (parseFloat(editing.qty) || 0)))}</span>
                </div>
                {editingBand.floor > 0 && (
                  <div className="text-xs mt-1">
                    Stop at {formatNPR(Math.round(editingBand.floor))} per{" "}
                    {BASIS_LABELS[editing.basis].np} · tag{" "}
                    {formatNPR(Math.round(editingBand.tag))}
                  </div>
                )}
                {editingStanding === "below" && (
                  <div className="text-xs mt-1 font-medium">
                    The owner's PIN will be asked for at checkout.
                  </div>
                )}
                {editingPieces !== null && editing.basis !== BASIS_PIECE && (
                  <div className="text-xs mt-1">
                    Takes {formatQty(editingPieces)} pieces off the shelf
                  </div>
                )}
              </div>

              <button
                onClick={commitEditor}
                className="w-full h-14 bg-[#8B0000] text-white text-lg font-semibold rounded-xl active:scale-95 transition-transform"
                data-testid="commit-line"
              >
                {editing.lineId ? "Update line" : "Add to cart"}
              </button>
            </div>
          )}
        </Modal>

        {/* Numpad for whichever field is being typed */}
        <Modal
          isOpen={Boolean(numpadTarget)}
          onClose={() => setNumpadTarget(null)}
          title={numpadTarget === "rate" ? "Price" : "Quantity"}
          titleNp={numpadTarget === "rate" ? "मूल्य" : "संख्या"}
        >
          <Numpad
            value={numpadValue}
            onChange={setNumpadValue}
            onConfirm={confirmNumpad}
            maxLength={7}
            // Kilos and metres divide; pieces and rupees do not need to, but a
            // bargained price can land on paisa, so the point is offered there.
            allowDecimal={
              numpadTarget === "rate" ||
              editing?.basis === BASIS_WEIGHT ||
              Boolean(units[editing?.product?.unit]?.allow_decimal)
            }
          />
        </Modal>

        {/* Checkout Modal */}
        <Modal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          title="Checkout"
          titleNp="बिल"
        >
          <div className="space-y-4">
            {/* Cart Items */}
            <div className="max-h-48 overflow-y-auto space-y-2">
              {cart.map((item) => {
                const product = products.find((p) => p.id === item.product_id);
                const band = product ? bandFor(product, item.price_basis) : null;
                const under = band && band.floor > 0 && item.rate < band.floor;
                return (
                  <div
                    key={item.line_id}
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      under ? "bg-red-50 border border-red-200" : "bg-gray-50"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {item.product_name}
                        {item.size && (
                          <span className="ml-1 font-nepali text-[#8B0000]">
                            {item.size}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatNPR(item.rate)} ×{" "}
                        {formatQty(item.basis_quantity)}{" "}
                        {BASIS_LABELS[item.price_basis].np}
                        {item.price_basis !== BASIS_PIECE &&
                          ` (${formatQty(item.pieces)} थान)`}
                      </p>
                      {under && (
                        <p className="text-xs text-red-600 font-medium">
                          below {formatNPR(Math.round(band.floor))}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-[#8B0000]">
                        {formatNPR(item.total)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() =>
                            product && openEditor(product, item)
                          }
                          className="p-1 bg-gray-200 rounded-lg"
                          title="Change quantity or price"
                          data-testid={`edit-line-${item.line_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => stepLine(item.line_id, -1)}
                          className="p-1 bg-gray-200 rounded-lg"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => stepLine(item.line_id, 1)}
                          className="p-1 bg-gray-200 rounded-lg"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeLine(item.line_id)}
                          className="p-1 text-red-500 ml-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Discount */}
            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                Discount / छुट (Rs.)
              </label>
              <input
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                className="w-full h-12 px-4 border border-gray-300 rounded-xl"
                data-testid="discount-input"
              />
              <p className="text-xs text-gray-400 mt-1">
                A round-off at the end. Bargaining goes on the line itself.
              </p>
            </div>

            {/* Customer Name (for credit) */}
            <div>
              <label className="text-sm text-gray-500 mb-1 block">
                Customer name / ग्राहकको नाम
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                className="w-full h-12 px-4 border border-gray-300 rounded-xl"
                data-testid="customer-name-input"
              />
            </div>

            {/* Payment type */}
            <div className="flex gap-2">
              <button
                onClick={() => setPaymentType("cash")}
                className={`flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                  paymentType === "cash"
                    ? "bg-[#8B0000] text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
                data-testid="payment-cash"
              >
                <Banknote className="w-5 h-5" />
                Cash / नगद
              </button>
              <button
                onClick={() => setPaymentType("credit")}
                className={`flex-1 py-3 rounded-xl font-medium flex items-center justify-center gap-2 ${
                  paymentType === "credit"
                    ? "bg-[#8B0000] text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
                data-testid="payment-credit"
              >
                <CreditCard className="w-5 h-5" />
                Credit / उधारो
              </button>
            </div>

            {/* Totals */}
            <div className="p-4 bg-gray-50 rounded-xl space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span>{formatNPR(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Discount</span>
                  <span>-{formatNPR(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-1 border-t border-gray-200">
                <span>Total</span>
                <span className="text-[#8B0000]">{formatNPR(total)}</span>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              disabled={submitting}
              className="w-full h-14 bg-[#8B0000] text-white text-lg font-semibold rounded-xl
                         active:scale-95 transition-transform disabled:opacity-50
                         flex items-center justify-center gap-2"
              data-testid="confirm-sale-btn"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Complete sale / बिक्री पूरा
                </>
              )}
            </button>
          </div>
        </Modal>

        {/* Owner's PIN, asked only when a line is under its floor. Not stored:
            the point is that a cashier cannot wave the limit through alone. */}
        <Modal
          isOpen={Boolean(pinPrompt)}
          onClose={() => {
            setPinPrompt(null);
            setPin("");
          }}
          title="Owner's PIN needed"
          titleNp="मालिकको PIN चाहिन्छ"
        >
          <div className="space-y-4">
            <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm flex items-start gap-2">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{pinPrompt?.detail}</span>
            </div>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="PIN"
              className="w-full h-14 px-4 border border-gray-300 rounded-xl text-center text-2xl tracking-widest"
              data-testid="override-pin-input"
            />
            <button
              onClick={() => submitSale(pin)}
              disabled={submitting || !pin}
              className="w-full h-14 bg-[#8B0000] text-white text-lg font-semibold rounded-xl
                         active:scale-95 transition-transform disabled:opacity-50"
              data-testid="override-confirm"
            >
              Approve and sell
            </button>
            <button
              onClick={() => {
                setPinPrompt(null);
                setPin("");
                setShowCheckout(true);
              }}
              className="w-full h-12 text-gray-600 font-medium"
            >
              Go back and change the price
            </button>
          </div>
        </Modal>
      </div>
    </Layout>
  );
}
